/**
 * Customer-identity verification — channel-agnostic abstraction.
 *
 * Sprint A (Feb 2026) ships with **email** only. The shape below is
 * deliberately future-compatible so adding `sms` (post Twilio A2P 10DLC)
 * or compound `email + sms` flows is a non-breaking change.
 *
 *   requiredChannels(orderType)  → string[]   // which channels must be verified
 *   issuedTokens                 → { [channel]: token }
 *   verifiedAt                   → { [channel]: ISO timestamp }
 *
 * Backend contract (checkout payload):
 *   verification: {
 *     channel: "email",                       // primary channel for legacy clients
 *     checkout_attempt_token: "<email-token>",
 *     channels: { email: { token: "..." } },  // future-compatible map
 *   }
 *
 * If/when SMS lands, add `"sms"` to `requiredChannels` for the order
 * mode and the modal will iterate over each pending channel.
 */

// ---------------------------------------------------------------------------
// channel rules
// ---------------------------------------------------------------------------

/**
 * Returns the list of channels that MUST be verified for the given order
 * mode. The function is the single source of truth for identity rules
 * across the storefront — keep it boring and explicit.
 *
 * Go-Live Sprint (Feb 2026, item #7 correction) — verification is now
 * driven by the tenant's per-mode catalog rules (single source of
 * truth), NOT hard-coded to the order type. Checkout must not
 * silently introduce a requirement that was not shown at the welcome
 * step. Rule: email is verified via OTP if and only if the mode has
 * `require_email` set. Cash always skips email regardless (staff
 * collects at counter).
 *
 * Delivery-phone-verification checkout fix (Aug 2026) — `"sms"` is now
 * included for Local Delivery orders when, and only when, the backend's
 * own authoritative condition is true: `deliveryPhoneVerificationRequired`
 * is the exact precomputed boolean the storefront receives from
 * `GET /{slug}/catalog`'s `tenant.delivery_config.
 * delivery_phone_verification_required` (`routes/retail_public.py`),
 * itself derived from the SAME `retail/delivery/serviceability.py::
 * is_delivery_phone_verification_required()` helper the order-
 * submission gate's condition mirrors (`retail/orders.py`). This is
 * deliberately NOT the same thing as "address validation is enabled" —
 * that alone is insufficient; both the tenant's address-validation flag
 * AND its `require_verified_delivery_phone` flag must be on, and the
 * caller must pass the precomputed value, never re-derive it from
 * `address_validation_enabled` alone.
 *
 * Unlike email, the SMS requirement is NOT skipped for `paymentMethod
 * === "cash"` — `retail/orders.py`'s order-submission phone-verification
 * gate applies no payment-method condition at all, so narrowing it here
 * would silently diverge from what the backend actually enforces.
 *
 * When both channels are required they are returned in a fixed,
 * deterministic order — email first, then sms — matching
 * `buildVerificationPayload`'s existing "email is the primary channel
 * when present" convention below.
 */
export function getRequiredChannels(orderType, modeRules, paymentMethod, deliveryPhoneVerificationRequired) {
  const rules = modeRules || {};
  const pm = (paymentMethod || "").toLowerCase();
  const channels = [];
  if (pm !== "cash" && rules.require_email) channels.push("email");
  if (orderType === "delivery" && deliveryPhoneVerificationRequired) channels.push("sms");
  return channels;
}

/**
 * For each channel, returns its label + which customer field it should
 * read from. Keeps the modal UI declarative.
 */
export function getChannelMeta(channel) {
  switch (channel) {
    case "email":
      return {
        key: "email",
        label: "Email",
        readField: "email",
        icon: "✉️",
        description: "We'll send a 6-digit code to verify your email.",
        placeholder: "you@example.com",
        inputType: "email",
      };
    case "sms":
      return {
        key: "sms",
        label: "Phone",
        readField: "phone",
        icon: "📱",
        description: "We'll text a 6-digit code to your phone.",
        placeholder: "+1 555 123 4567",
        inputType: "tel",
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// payload builder
// ---------------------------------------------------------------------------

/**
 * Build the `verification` block for the checkout POST body. Stays
 * back-compat with the Sprint A backend (single `checkout_attempt_token`)
 * while also forwarding the channels map for future readers.
 */
export function buildVerificationPayload(verifiedTokens) {
  const channels = {};
  for (const [ch, info] of Object.entries(verifiedTokens || {})) {
    if (info?.token) {
      channels[ch] = { token: info.token, verified_at: info.verifiedAt || null };
    }
  }
  const primaryChannel = channels.email ? "email" : (Object.keys(channels)[0] || null);
  if (!primaryChannel) return null;
  return {
    channel: primaryChannel,
    checkout_attempt_token: channels[primaryChannel].token,
    channels,
  };
}
